import { useMemo, useRef, useCallback, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'store';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import {
  CustomFieldDefinition,
  RecordTypes,
} from 'ui-component/DetailCustomFields';
import _ from 'lodash';
import {
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { FieldValues } from 'react-hook-form';
import { ExtendedGridColDef } from 'types/datagrid';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

const getBaseProps = (f: CustomFieldDefinition) => ({
  field: `customField_${f.fieldName}`,
  headerName: f.fieldLabel,
  description: `Custom Field: ${f.fieldLabel}`,
  type: f.fieldType,
  minWidth: 100,
  hideable: true,
  flex: 0.5,
  editable: true,
  aggregable: false,
  valueGetter: (params: GridValueGetterParams) =>
    _.get(
      params.row?.externalMetadata?.customFields,
      f.fieldName,
      f.defaultValue
    ),
});

export default function useGetCustomFieldColumns({
  recordType,
  useMutation,
  getMutationArgs,
}: {
  recordType: RecordTypes;
  useMutation?: UseMutation<any>;
  getMutationArgs?: (data: FieldValues, params: GridRenderEditCellParams) => {};
}) {
  const tempCustomProperties = useFeature(FeatureFlags.tempCustomProperties).on;
  const { activeOrg } = useSelector((state) => state.org);

  const getMutationArgsRef = useRef(getMutationArgs);
  const memoizedGetMutationArgs = useCallback(
    (data: FieldValues, params: GridRenderEditCellParams) =>
      getMutationArgsRef.current?.(data, params),
    [getMutationArgsRef]
  );
  useEffect(() => {
    getMutationArgsRef.current = getMutationArgs;
  }, [getMutationArgs]);

  const { data: metadataOrg, isLoading: isLoadingMetadataOrg } = useGetOrgQuery(
    activeOrg?.createdFrom
      ? typeof activeOrg.createdFrom === 'string'
        ? activeOrg.createdFrom
        : activeOrg.createdFrom.id
      : skipToken
  );

  const customFieldDefinitions = useMemo<CustomFieldDefinition[]>(
    () =>
      tempCustomProperties
        ? []
        : _.get(
            (metadataOrg || activeOrg)?.internalMetadata,
            ['customFields', recordType],
            []
          ),
    [tempCustomProperties, metadataOrg, activeOrg, recordType]
  );

  const customFieldColumns = useMemo<ExtendedGridColDef[]>(
    () =>
      !!useMutation && !!memoizedGetMutationArgs
        ? customFieldDefinitions.map((f) => ({
            ...getBaseProps(f),
            inlineEditRenderCell: true,
            useMutation,
            getMutationArgs: memoizedGetMutationArgs,
            mutationPreSubmit: (
              data: FieldValues,
              params: GridRenderCellParams
            ) => {
              let currentMetadata =
                _.cloneDeep(params.row.externalMetadata) || {};
              currentMetadata = _.set(
                currentMetadata,
                ['customFields', f.fieldName],
                _.get(data, `customField_${f.fieldName}`)
              );
              return {
                externalMetadata: currentMetadata,
              };
            },
          }))
        : customFieldDefinitions.map(getBaseProps),
    [useMutation, memoizedGetMutationArgs, customFieldDefinitions]
  );

  return { isFetching: isLoadingMetadataOrg, customFieldColumns };
}
